import { showSimpleMessage } from '@/common/util';
import reportesApiService from '@/utils/reportesApi';
import { ReportesHelpers } from '@/utils/reportesHelpers';

/**
 * Servicio para manejar la lógica de reportes especiales
 */
class ReportesEspecialesService {
  /**
   * Valida que se haya seleccionado un fideicomiso específico
   */
  validarFideicomisoUnico(filtroFideicomiso, nombreReporte) {
    if (!filtroFideicomiso || filtroFideicomiso === null) {
      showSimpleMessage(
        'Fideicomiso Requerido',
        `Debe seleccionar un fideicomiso específico para generar el reporte ${nombreReporte}.`,
        'warning',
        3000
      );
      return false;
    }

    if (filtroFideicomiso === 'Todas') {
      showSimpleMessage(
        'Selección Específica Requerida',
        `Para ${nombreReporte} debe seleccionar un fideicomiso específico, no "Todas".`,
        'warning',
        3000
      );
      return false;
    }

    return true;
  }

  /**
   * Formatea la respuesta del reporte
   */
  formatearRespuesta(resultado, datosAplanados, nombreReporte) {
    return {
      tablaMockeada: {
        statusCode: 200,
        timestamp: new Date().toISOString(),
        status: 'success',
        resultados: datosAplanados.resultados,
        totales: resultado.datos.totales || {},
        response: {
          pagination: {
            currentPage: resultado.page,
            totalRecords: resultado.totalItems,
            limit: resultado.limit,
          },
          resultados: resultado.datos.resultados,
          total: resultado.totalItems,
        },
      },
      totalItems: resultado.totalItems,
      reporteEspecialActivo: nombreReporte,
    };
  }

  /**
   * Genera reporte Info Mora (Financiamiento)
   */
  async generarInfoMora(contexto, aplanarDatos, mostrarMensaje = true) {
    console.log('🎯 generarInfoMoraFinanciamiento - filtroFideicomiso:', contexto.filtroFideicomiso);

    // Validar fideicomiso
    if (!this.validarFideicomisoUnico(contexto.filtroFideicomiso, 'Info Mora (Financiamiento)')) {
      return null;
    }

    try {
      const parametros = ReportesHelpers.construirParametrosReporte({
        ...contexto,
        page: contexto.paginaActual,
        limit: contexto.itemsPorPagina,
      });

      console.log('Info Mora - Parámetros enviados:', parametros);

      const resultado = await reportesApiService.obtenerInfoMoraFinanciamiento(parametros);

      console.log('🔍 Info Mora - Respuesta completa:', resultado);
      console.log('🔍 Info Mora - Primer resultado departamento:', resultado.datos && resultado.datos.resultados && resultado.datos.resultados[0] && resultado.datos.resultados[0].departamento);

      // Aplanar datos
      console.log('🔍 ANTES de aplanarDatos - primer item:', resultado.datos && resultado.datos.resultados && resultado.datos.resultados[0]);
      const datosAplanados = aplanarDatos(resultado.datos);
      console.log('🔍 DESPUÉS de aplanarDatos - primer item:', datosAplanados && datosAplanados.resultados && datosAplanados.resultados[0]);

      const respuesta = this.formatearRespuesta(resultado, datosAplanados, 'Info Mora (Financiamiento)');

      if (mostrarMensaje) {
        showSimpleMessage(
          'Reporte Generado Exitosamente',
          'Se generó el reporte Info Mora (Financiamiento)',
          'success',
          1500
        );
      }

      return respuesta;
    } catch (error) {
      console.error('Error al generar info mora financiamiento:', error);

      const mensajeError = (error.response && error.response.data && error.response.data.message) ||
                          error.message ||
                          'Error al generar el reporte Info Mora';

      showSimpleMessage('Error en Reporte', mensajeError, 'error', 3000);
      throw error;
    }
  }

  /**
   * Genera reporte Info Cobranzas Proyectadas
   */
  async generarInfoCobranzasProyectadas(contexto, procesarDatos, mostrarMensaje = true) {
    console.log('🎯 generarInfoCobranzasProyectadas - filtros aplicados');

    // Validar fideicomiso
    if (!this.validarFideicomisoUnico(contexto.filtroFideicomiso, 'Info Cobranzas Proyectadas')) {
      return null;
    }

    try {
      const parametros = ReportesHelpers.construirParametrosReporte({
        ...contexto,
        page: contexto.paginaActual,
        limit: contexto.itemsPorPagina,
      });

      console.log('Info Cobranzas Proyectadas - Parámetros enviados:', parametros);

      const resultado = await reportesApiService.obtenerInfoCobranzasProyectadas(parametros);

      console.log('🔍 Info Cobranzas Proyectadas - Respuesta completa:', resultado);
      console.log('🔍 Info Cobranzas Proyectadas - Primeros 3 resultados:', resultado.datos.resultados.slice(0, 3));

      // Procesar datos usando la función específica (procesarDatosCobranzasProyectadas)
      const resultadosProcesados = procesarDatos(resultado.datos.resultados || []);
      console.log('🔄 Info Cobranzas Proyectadas - Datos procesados:', resultadosProcesados.slice(0, 2));

      const respuesta = {
        tablaMockeada: {
          statusCode: 200,
          timestamp: new Date().toISOString(),
          status: 'success',
          resultados: resultadosProcesados,
          totales: resultado.datos.totales || {},
          response: {
            pagination: {
              currentPage: resultado.page,
              totalRecords: resultado.totalItems,
              limit: resultado.limit,
            },
            resultados: resultado.datos.resultados,
            total: resultado.totalItems,
          },
        },
        totalItems: resultado.totalItems,
        reporteEspecialActivo: 'Info Cobranzas Proyectadas',
      };

      if (mostrarMensaje) {
        showSimpleMessage(
          'Reporte Generado Exitosamente',
          'Se generó el reporte Info Cobranzas Proyectadas',
          'success',
          1500
        );
      }

      return respuesta;
    } catch (error) {
      console.error('Error al generar info cobranzas proyectadas:', error);

      const mensajeError = (error.response && error.response.data && error.response.data.message) ||
                          error.message ||
                          'Error al generar el reporte Info Cobranzas Proyectadas';

      showSimpleMessage('Error en Reporte', mensajeError, 'error', 3000);
      throw error;
    }
  }

  /**
   * Genera reporte Info Cobranzas
   */
  async generarInfoCobranzas(contexto, procesarDatos, mostrarMensaje = true) {
    console.log('🎯 generarInfoCobranzas - filtros aplicados');

    // Validar fideicomiso
    if (!this.validarFideicomisoUnico(contexto.filtroFideicomiso, 'Info Cobranzas')) {
      return null;
    }

    try {
      const parametros = ReportesHelpers.construirParametrosReporte({
        ...contexto,
        page: contexto.paginaActual,
        limit: contexto.itemsPorPagina,
      });

      console.log('Info Cobranzas - Parámetros enviados:', parametros);
      console.log('Info Cobranzas - Sin barra de progreso', 'itemsPorPagina:', contexto.itemsPorPagina);

      const resultado = await reportesApiService.obtenerInfoCobranzas(parametros, null);

      console.log('🔍 Info Cobranzas - Respuesta completa:', resultado);
      console.log('🚀🚀🚀 DEBUGGING TOTAL ITEMS - resultado.totalItems:', resultado.totalItems);

      // Adaptar la estructura de respuesta que viene del endpoint
      const datosOriginales = (resultado.response && resultado.response.data) ||
                             (resultado.datos && resultado.datos.resultados) || [];

      console.log('🔍 Info Cobranzas - Datos originales:', datosOriginales);
      console.log('🚀 TOTAL ITEMS DEBUG - resultado.totalItems:', resultado.totalItems);
      console.log('🚀 TOTAL ITEMS DEBUG - datosOriginales.length:', datosOriginales.length);

      // Procesar datos para crear propiedades de periodo
      const datosProcesados = procesarDatos(datosOriginales);
      console.log('🔍 Info Cobranzas - Datos procesados:', datosProcesados);

      const respuesta = {
        tablaMockeada: {
          statusCode: 200,
          timestamp: new Date().toISOString(),
          status: 'success',
          resultados: datosProcesados,
          totales: (resultado.datos && resultado.datos.totales) || {},
          response: {
            pagination: {
              currentPage: resultado.page || 1,
              totalRecords: resultado.totalItems,
              limit: resultado.limit || contexto.itemsPorPagina,
            },
            resultados: datosOriginales,
            total: resultado.totalItems,
          },
        },
        totalItems: resultado.totalItems,
        reporteEspecialActivo: 'Info Cobranzas',
      };

      if (mostrarMensaje) {
        showSimpleMessage(
          'Reporte Generado Exitosamente',
          'Se generó el reporte Info Cobranzas',
          'success',
          1500
        );
      }

      return respuesta;
    } catch (error) {
      console.error('Error al generar info cobranzas:', error);

      const mensajeError = (error.response && error.response.data && error.response.data.message) ||
                          error.message ||
                          'Error al generar el reporte Info Cobranzas';

      showSimpleMessage('Error en Reporte', mensajeError, 'error', 3000);
      throw error;
    }
  }

  /**
   * Genera reporte Info Cte y No Cte
   */
  async generarInfoCteYNoCte(contexto, mostrarMensaje = true) {
    console.log('🎯 generarInfoCteYNoCte - filtros aplicados');

    // Validar fideicomiso
    if (!this.validarFideicomisoUnico(contexto.filtroFideicomiso, 'Info Cte y No Cte')) {
      return null;
    }

    try {
      // Primero obtener la fecha disponible del periodo
      let fechaDisponible = null;
      try {
        const fechaInfo = await reportesApiService.obtenerFechasDisponiblesCteYNoCte();
        if (fechaInfo && fechaInfo.available_date) {
          // Convertir formato "30-06" + año ACTUAL a fecha ISO "YYYY-MM-DD"
          const [dia, mes] = fechaInfo.available_date.split('-');
          const añoActual = Number(mes) === 12 && fechaInfo.reference_year
            ? fechaInfo.reference_year
            : new Date().getFullYear();
          fechaDisponible = `${añoActual}-${mes}-${dia}`;
          console.log('📅 Fecha de corte calculada:', fechaDisponible, 'usando año actual:', añoActual);
          console.log('📅 Período disponible del backend:', fechaInfo);
        }
      } catch (fechaError) {
        console.warn('⚠️ Error al obtener fecha disponible, usando fecha actual:', fechaError);
      }

      const parametros = ReportesHelpers.construirParametrosReporte({
        ...contexto,
        page: contexto.paginaActual,
        limit: contexto.itemsPorPagina,
        fechaDisponibleCteYNoCte: fechaDisponible, // Pasar la fecha disponible
      });

      console.log('Info Cte y No Cte - Parámetros enviados:', parametros);

      const resultado = await reportesApiService.obtenerInfoCteYNoCte(parametros);

      console.log('🔍 Info Cte y No Cte - Respuesta completa:', resultado);

      // Adaptar la estructura de respuesta que viene del endpoint
      const datosOriginales = resultado.datos.resultados || [];

      console.log('🔍 Info Cte y No Cte - Datos originales:', datosOriginales);
      console.log('🔍 Info Cte y No Cte - Total items:', resultado.totalItems);
      if (datosOriginales.length > 0) {
        console.log('🔍 Primer elemento de datos:', datosOriginales[0]);
        console.log('🔍 monto_otorgado del primer elemento:', datosOriginales[0].monto_otorgado);
      }

      const respuesta = {
        tablaMockeada: {
          statusCode: 200,
          timestamp: new Date().toISOString(),
          status: 'success',
          resultados: datosOriginales,
          totales: resultado.datos.totales || {},
          response: {
            pagination: {
              currentPage: resultado.page || 1,
              totalRecords: resultado.totalItems || datosOriginales.length,
              limit: resultado.limit || contexto.itemsPorPagina,
            },
            resultados: datosOriginales,
            total: resultado.totalItems || datosOriginales.length,
          },
        },
        totalItems: resultado.totalItems || datosOriginales.length,
        reporteEspecialActivo: 'Info Cte y No Cte',
        fechaDisponibleCteYNoCte: fechaDisponible,
      };

      console.log('🎯 Debug final Info Cte y No Cte:');
      console.log('- totalItems:', respuesta.totalItems);
      console.log('- datosOriginales length:', datosOriginales.length);

      if (mostrarMensaje) {
        showSimpleMessage(
          'Reporte Generado Exitosamente',
          'Se generó el reporte Info Cte y No Cte',
          'success',
          1500
        );
      }

      return respuesta;
    } catch (error) {
      console.error('Error al generar info cte y no cte:', error);

      const mensajeError = (error.response && error.response.data && error.response.data.message) ||
                          error.message ||
                          'Error al generar el reporte Info Cte y No Cte';

      showSimpleMessage('Error en Reporte', mensajeError, 'error', 3000);
      throw error;
    }
  }

  /**
   * Genera reporte Saldos HTC
   */
  async generarSaldosHTC(contexto, mostrarMensaje = true) {
    console.log('🎯 generarSaldosHTC - filtros aplicados');

    // Validar fideicomiso
    if (!this.validarFideicomisoUnico(contexto.filtroFideicomiso, 'Reporte de Saldos - HTC')) {
      return null;
    }

    try {
      // Primero obtener la fecha disponible del periodo
      let fechaDisponible = null;
      try {
        const fechaInfo = await reportesApiService.obtenerFechasDisponiblesSaldosHTC();
        if (fechaInfo && fechaInfo.available_date) {
          // Convertir formato "30-06" + año ACTUAL a fecha ISO "YYYY-MM-DD"
          const [dia, mes] = fechaInfo.available_date.split('-');
          const añoActual = new Date().getFullYear(); // Usar año en curso
          fechaDisponible = `${añoActual}-${mes}-${dia}`;
          console.log('📅 Saldos HTC - Fecha de corte calculada:', fechaDisponible, 'usando año actual:', añoActual);
          console.log('📅 Saldos HTC - Período disponible del backend:', fechaInfo);
        }
      } catch (fechaError) {
        console.warn('⚠️ Error al obtener fecha disponible, usando fecha actual:', fechaError);
      }

      const parametros = ReportesHelpers.construirParametrosReporte({
        ...contexto,
        page: contexto.paginaActual,
        limit: contexto.itemsPorPagina,
        fechaDisponibleSaldosHTC: fechaDisponible, // Pasar la fecha disponible
      });

      console.log('Saldos HTC - Parámetros enviados:', parametros);

      const resultado = await reportesApiService.obtenerSaldosHTC(parametros);

      console.log('🔍 Saldos HTC - Respuesta completa:', resultado);

      // Adaptar la estructura de respuesta que viene del endpoint
      const datosOriginales = resultado.datos.resultados || [];

      console.log('🔍 Saldos HTC - Datos originales:', datosOriginales);
      console.log('🔍 Saldos HTC - Total items:', resultado.totalItems);
      if (datosOriginales.length > 0) {
        console.log('🔍 Saldos HTC - Primer elemento de datos:', datosOriginales[0]);
      }

      const respuesta = {
        tablaMockeada: {
          statusCode: 200,
          timestamp: new Date().toISOString(),
          status: 'success',
          resultados: datosOriginales,
          totales: resultado.datos.totales || {},
          response: {
            pagination: {
              currentPage: resultado.page || 1,
              totalRecords: resultado.totalItems || datosOriginales.length,
              limit: resultado.limit || contexto.itemsPorPagina,
            },
            resultados: datosOriginales,
            total: resultado.totalItems || datosOriginales.length,
          },
        },
        totalItems: resultado.totalItems || datosOriginales.length,
        reporteEspecialActivo: 'Reporte de Saldos - HTC',
        fechaDisponibleSaldosHTC: fechaDisponible,
        periodoSaldosHTC: resultado.periodo || null, // Periodo del reporte HTC
      };

      console.log('🎯 Debug final Saldos HTC:');
      console.log('- totalItems:', respuesta.totalItems);
      console.log('- datosOriginales length:', datosOriginales.length);

      if (mostrarMensaje) {
        showSimpleMessage(
          'Reporte Generado Exitosamente',
          'Se generó el reporte Saldos HTC',
          'success',
          1500
        );
      }

      return respuesta;
    } catch (error) {
      console.error('Error al generar saldos HTC:', error);

      const mensajeError = (error.response && error.response.data && error.response.data.message) ||
                          error.message ||
                          'Error al generar el reporte Saldos HTC';

      showSimpleMessage('Error en Reporte', mensajeError, 'error', 3000);
      throw error;
    }
  }
}

export default new ReportesEspecialesService();
